import { createContext, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { UserInfo, UserInfoData, UserInfoProps } from 'etc/types';
import { useUserData } from 'hooks/useUserData';
import { useReporterInfo } from 'hooks/useReporterInfo';
import { useMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import { ErrorMessage } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { getReportOriginTypeFromPath } from 'constants/routes';

import { EmailType } from '../../proto/generated/spotify/tintin/reporting/v1/common';

export const UserInfoContext = createContext<UserInfo>({} as UserInfo);
export const useUserInfo = () => useContext(UserInfoContext);

export const withUserInfo = <P extends UserInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof UserInfoProps>) => (
    <UserInfoContext.Consumer>{userInfo => <Comp {...(props as P)} userInfo={userInfo} />}</UserInfoContext.Consumer>
  );
};

const UserInfoProvider: React.FC = ({ children }) => {
  const [userProvidedEmail, setUserProvidedEmail] = useState<string>();
  const [refreshReporterInfo, setRefreshReporterInfo] = useState<boolean>(false);
  const [userProvidedCountry, setUserProvidedCountry] = useState<string>();
  const [userEmailType, setUserEmailType] = useState<EmailType>(EmailType.EMAIL_TYPE_INVALID);

  const location = useLocation();
  const reportOriginType = useMemo(() => getReportOriginTypeFromPath(location.pathname), [location.pathname]);
  const { isLoading, error, userData } = useUserData();
  const {
    emailType,
    country,
    error: useReporterError,
    isLoading: reporterInfoLoading,
  } = useReporterInfo(userData?.username, userProvidedEmail, reportOriginType, refreshReporterInfo);

  const { market } = useMarketInfo();

  const data: UserInfoData = {
    username: userData?.username,
    email: userData?.email,
    emailType: emailType,
    country: country || userProvidedCountry,
  };

  const isRateLimited = String(useReporterError) === ErrorMessage.RATE_LIMITING_ERROR;

  return (
    <UserInfoContext.Provider
      value={{
        loading: isLoading,
        error: !!error,
        isRateLimited,
        isUserCountryError: (!!useReporterError && !isRateLimited) || (!country && !reporterInfoLoading),
        data,
        username: data.username,
        userIsLoggedIn: !!data.username,
        userCountryIsoCode: data.country || (market?.split('-').shift() as string),
        userAccountEmail: data.email,
        userEmailType,
        setUserEmailType,
        setUserProvidedEmail,
        setRefreshReporterInfo,
        refreshReporterInfo,
        setUserProvidedCountry,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export default UserInfoProvider;
